import { useQuery } from '@tanstack/react-query';

import { daysBehind as computeDaysBehind, DEV_VERSION, isNewVersion } from '@repo/utils/app-version';

const ONE_HOUR_MS = 1000 * 60 * 60;

export type UseAppVersionOptions = {
  /** The version baked into the running bundle (`import.meta.env.VITE_VERSION`). */
  currentVersion: string;
  /** How often to re-fetch `/version.txt`. Defaults to one hour. */
  pollIntervalMs?: number | undefined;
};

export type AppVersionState = {
  currentVersion: string;
  latestVersion: string | undefined;
  newVersionFound: boolean;
  /** Whole days the running build is behind the deployed build, or null. */
  daysBehind: number | null;
};

async function fetchLatestVersion(): Promise<string> {
  // `no-store` plus the no-cache header on the SWA route guarantees a stale
  // tab reads the freshly deployed number, not the one baked into its bundle.
  const response = await fetch('/version.txt', { cache: 'no-store' });

  if (!response.ok) {
    throw new Error(`Failed to fetch version.txt: ${response.status}`);
  }

  return (await response.text()).trim();
}

/**
 * Polls `/version.txt` and compares it to the version baked into the running
 * bundle. Disabled entirely when `currentVersion` is the local-dev sentinel.
 */
export function useAppVersion({
  currentVersion,
  pollIntervalMs = ONE_HOUR_MS,
}: UseAppVersionOptions): AppVersionState {
  const enabled = currentVersion !== DEV_VERSION;

  const { data: latestVersion } = useQuery({
    queryKey: ['app-version'],
    queryFn: fetchLatestVersion,
    enabled,
    refetchInterval: pollIntervalMs,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: false,
  });

  const newVersionFound = latestVersion !== undefined
    && isNewVersion(currentVersion, latestVersion);

  return {
    currentVersion,
    latestVersion,
    newVersionFound,
    daysBehind: newVersionFound && latestVersion
      ? computeDaysBehind(currentVersion, latestVersion)
      : null,
  };
}
